import React, {useState, useEffect} from 'react';
import CPageModule from '../../../components/CPageModule';
import PageModuleFormGroup from '../../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../../components/PageModuleFormRow';
import Page from '../../../components/layout/Page';
import {type IAccessLinkInquiryDetail} from '../../../models/IAccessLinkInquiryDetail';
import {type IFactsheetInquiryUpdateResponse} from '../../../models/IAccessLinkInquiryUpdateResponse';
import {useParams, useNavigate, Link } from 'react-router-dom';
import {updateStateAttribute} from '../../../helpers/helper';
import PublicFactsheetServices from '../../../services/publicFactsheetServices';
import CRadioGroup from '../../../components/CRadioGroup';
import CButton from '../../../components/CButton';
import {ArrowForward, Delete} from '@mui/icons-material';
import CLocalizedText from '../../../components/CLocalizedText';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {updateSnackbar} from '../../../store/appSlice';
import CDeleteConfirmationButton from '../../../components/CDeleteConfirmationButton';
import {type IRequestState} from '../../../models/IRequestState';
import NotFoundModule from '../../../components/PageGates/NotFoundModule';
import { PageRoutes } from '../../../enums/enums';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import CTextFieldDisplay from '../../../components/CTextFieldDisplay';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import HGeneric from '../../../helpers/HGeneric';

const updateInquiryOptions = {
  opt1: {
    label: <CLocalizedText dictKey={'radioBtn.update-options.opt1.label'}/>,
    description: <CLocalizedText dictKey={'radioBtn.update-options.opt1.desc'}/>,
    value: 'APPROVED',
  },
  opt2: {
    label: <CLocalizedText dictKey={'radioBtn.update-options.opt2.label'}/>,
    description: <CLocalizedText dictKey={'radioBtn.update-options.opt2.desc'}/>,
    value: 'DENIED',
  },
} as const;

interface State {
  factSheetLinkInquiryDetail?: Partial<IAccessLinkInquiryDetail>;
  requestState: IRequestState;
  requestStateForUpdate:IRequestState;
  requestStateForDelete:IRequestState;
  inquiryNotFound: boolean;
  updateOptions: keyof typeof updateInquiryOptions | null;
}

export default function SingleAccessLinkInquiryView() {
  const {inquiryId} = useParams();
  const id = inquiryId as string;

  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const isMobile = dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_1;
  const gridColsStyle = isMobile ? 'repeat(1, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))';



  const [state, setState] = useState<State>({
    factSheetLinkInquiryDetail: {},
    requestState: {isError: false, isLoading: false},
    requestStateForUpdate: {isError: false, isLoading: false},
    requestStateForDelete: {isError: false, isLoading: false},
    inquiryNotFound: false,
    updateOptions: null,
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);


  const statusCreated = CLocalizedTextString('textField.label.status.created');
  const statusApproved = CLocalizedTextString('textField.label.status.approved');
  const statusDenied = CLocalizedTextString('textField.label.status.denied');
  const labelAccessLinkInquiry = CLocalizedTextString('fs.inquiry.link');

  useEffect(() => {
    (async () => {
      updateStateAttribute<State>({requestState: {isLoading: true, isError: false}}, setState);

      const res = await PublicFactsheetServices.getInquiryDetails(id);

      if (res.hasError()) {
        updateStateAttribute<State>({
          requestState: {isLoading: false, isError: res.hasError()},
          inquiryNotFound: true
        }, setState);
      }

      if (!res.hasError()) {
        updateStateAttribute<State>({
          factSheetLinkInquiryDetail: res.getData(),
          requestState: {isLoading: false, isError: res.hasError()},
          updateOptions: res.getData().status === 'DENIED' ? 'opt2' : 'opt1',
        }, setState);
      }

    })();
  }, []);

  const updateHandler = async () => {
    if (!state.updateOptions) return;
    updateStateAttribute<State>({requestStateForUpdate: {isLoading: true, isError: false}}, setState);

    const form = {
      status: updateInquiryOptions[state.updateOptions].value,
    };

    const res = await PublicFactsheetServices.updateFactsheetLinkInquiryDetail(id, form);
    const data: IFactsheetInquiryUpdateResponse = res.getData();

    if (res.hasError()) {
      updateStateAttribute<State>({requestStateForUpdate: {isLoading: false, isError: res.hasError()}}, setState);
      dispatch(updateSnackbar({
        message: 'Unable to update factsheet',
        severity: 'error',
        isVisible: true,
      }));
    }

    if (data.success) {
      updateStateAttribute<State>({
        requestStateForUpdate: {isLoading: false, isError: false},
        factSheetLinkInquiryDetail: {
          ...state.factSheetLinkInquiryDetail,
          status: data.latest_status,
          factsheet_link_id: data.factsheet_link_id,
          view_count: data.view_count,
          status_assignee: data.status_assignee,
          status_timestamp: data.status_timestamp
        },
        updateOptions: data.latest_status === 'DENIED' ? 'opt2' : 'opt1',
      }, setState);
      dispatch(updateSnackbar({
        message: data.email_status?.message,
        severity: data.email_status?.status === false ? 'warning' : 'success',
        isVisible: true,
      }));
    }

  };

  const deleteHandler = async () => {

    updateStateAttribute<State>({requestStateForDelete: {isLoading: true, isError: false}}, setState);

    const res = await PublicFactsheetServices.deleteFactsheetInquiry(id);

    if (res.hasError()) {
      dispatch(updateSnackbar({
        message: 'Unable to delete factsheet',
        severity: 'error',
        isVisible: true,
      }));
    }

    if (res.getData().success) {
      navigate('/publishing/fs-links/inquiries');
    }

    updateStateAttribute<State>({requestStateForDelete: {isLoading: false, isError: res.hasError()}}, setState);
  };

  return (
    <>
      {
        state.inquiryNotFound ? <NotFoundModule/> :
          <Page title={[state.factSheetLinkInquiryDetail?.first_name, state.factSheetLinkInquiryDetail?.last_name].join(' ').trim()}>
            <CPageModule fullWidth apiRequestState={state.requestState}
                         showContent={!!state.factSheetLinkInquiryDetail}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h2>{labelAccessLinkInquiry}</h2>
                <div className={'grid gap-4'} style={{ gridTemplateColumns: gridColsStyle }}>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.firstName'} />}>
                    {state.factSheetLinkInquiryDetail?.first_name}
                  </CTextFieldDisplay>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.lastName'} />}>
                    {state.factSheetLinkInquiryDetail?.last_name}
                  </CTextFieldDisplay>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.company'} />}>
                    {state.factSheetLinkInquiryDetail?.company ?? '-'}
                  </CTextFieldDisplay>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.position'} />}>
                    {state.factSheetLinkInquiryDetail?.position ?? '-'}
                  </CTextFieldDisplay>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.email'} />}>
                    {state.factSheetLinkInquiryDetail?.email}
                  </CTextFieldDisplay>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.requestedTimestamp'} />}>
                    {
                      HGeneric.formatString(
                        state.factSheetLinkInquiryDetail?.created_timestamp,
                        { in: 'tzTimestamp', out: 'localTimestampExtended' }
                      )
                    }
                  </CTextFieldDisplay>

                  <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.factsheetLinkID'} />}>
                    {state.factSheetLinkInquiryDetail?.factsheet_link_id ? (
                      <span>
                      <Link
                        to={`/${PageRoutes.PUBLISHING_OUTLET}/${PageRoutes.PUBLISHING_FS_OUTLET}/${PageRoutes.PUBLISHING_FS_OVERVIEW_OUTLET}/${state.factSheetLinkInquiryDetail?.factsheet_link_id}`}
                        className="font-normal"
                      >
                        {state.factSheetLinkInquiryDetail?.factsheet_link_id}
                      </Link>
                      <span> ({state.factSheetLinkInquiryDetail?.view_count ?? 0})</span>
                    </span>
                    ) : (
                      '-'
                    )}
                  </CTextFieldDisplay>
                </div>
              </CPageModule>
              <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <h3>
                    <CLocalizedText dictKey={'inquiry.subheading.updateInquiry'} />
                  </h3>
                </CPageModule>
                <CPageModule submodule>
                  <div className={'grid gap-4'} style={{ gridTemplateColumns: gridColsStyle }}>
                    <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.status'} />}>
                      {state.factSheetLinkInquiryDetail?.status === 'CREATED'
                        ? statusCreated
                        : state.factSheetLinkInquiryDetail?.status === 'APPROVED'
                          ? statusApproved
                          : statusDenied}
                    </CTextFieldDisplay>
                    <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.latestStatusAssignee'} />}>
                      {state.factSheetLinkInquiryDetail?.status_assignee}
                    </CTextFieldDisplay>
                    <CTextFieldDisplay label={<CLocalizedText dictKey={'textField.label.latestStatusTimeStamp'} />}>
                      {
                        HGeneric.formatString(
                          state.factSheetLinkInquiryDetail?.status_timestamp,
                          { in: 'tzTimestamp', out: 'localTimestampExtended' }
                        )
                      }
                    </CTextFieldDisplay>
                  </div>

                  <PageModuleFormGroup>
                    <CRadioGroup
                      options={updateInquiryOptions}
                      value={state.updateOptions}
                      onChange={(key) => {
                        updateStateAttribute<State>({ updateOptions: key }, setState);
                      }}
                    />
                    <PageModuleFormRow description={<CLocalizedText dictKey={'accessLinkInquiry.update.btn.desc'} />}>
                      <CButton
                        style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
                        icons={{ end: <ArrowForward /> }}
                        onClick={async () => {
                          await updateHandler();
                        }}
                        disabled={
                          (!state.updateOptions ||
                          state.factSheetLinkInquiryDetail?.status === updateInquiryOptions[state.updateOptions].value)
                          || state.requestStateForUpdate.isLoading}
                        state={state.requestStateForUpdate}
                        isForm
                      >
                        <CLocalizedText dictKey={'accessLinkInquiry.update.btn'} />
                      </CButton>
                    </PageModuleFormRow>
                  </PageModuleFormGroup>
                </CPageModule>
              </CPageModule>


              <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <h3>
                    <CLocalizedText dictKey={'inquiry.subheading.deleteInquiry'} />
                  </h3>
                </CPageModule>
                <CPageModule submodule>
                  <PageModuleFormRow description={<CLocalizedText dictKey={'accessLinkInquiry.delete.btn.desc'} />}>
                    <CButton
                      style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
                      variant={'danger'}
                      icons={{ start: <Delete /> }}
                      onClick={() => {
                        setShowDeleteModal(true);
                      }}
                      disabled={(state.factSheetLinkInquiryDetail?.status !== updateInquiryOptions.opt2.value) ||
                        state.requestStateForDelete.isLoading
                      }
                      state={state.requestStateForDelete}
                      isForm
                    >
                      <CLocalizedText dictKey={'accessLinkInquiry.delete.btn'} />
                    </CButton>
                  </PageModuleFormRow>
                </CPageModule>
                {showDeleteModal && (
                  <CDeleteConfirmationButton
                    showModal={showDeleteModal}
                    onConfirm={async () => {
                      await deleteHandler();
                      setShowDeleteModal(false);
                    }}
                    onCancel={() => {
                      setShowDeleteModal(false);
                    }
                    }
                    title={'Are you sure you want to delete this inquiry?'}
                  />
                )}
              </CPageModule>
            </CPageModule>
          </Page>
      }
    </>
  );
}